import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { email as emailValidator, form, FormField, required, validate } from '@angular/forms/signals';
import { PrimeTemplate } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';

import { isValidUsPhone, PHONE_FORM_ERRORS } from '../../helpers/phone-validator';
import { YtFormControl, FormControlType } from '../form-control/form-control';
import { YButton } from '../y-button/y-button';
import { EditProfileModalService } from './edit-profile-modal.service';

interface EditProfileFormModel {
  firstName: string;
  lastName: string;
  role: string;
  email: string;
  phoneNumber: string;
}

const DEFAULT_EDIT_PROFILE_FORM: EditProfileFormModel = {
  firstName: '',
  lastName: '',
  role: '',
  email: '',
  phoneNumber: ''
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'yt-edit-profile-modal',
  templateUrl: './edit-profile-modal.html',
  styleUrls: ['./edit-profile-modal.scss'],
  imports: [Dialog, Avatar, YButton, InputText, PrimeTemplate, FormField, YtFormControl],
  standalone: true
})
export class EditProfileModal {
  readonly editProfileModalService = inject(EditProfileModalService);
  readonly FormControlType = FormControlType;

  private readonly formModel$ = signal<EditProfileFormModel>({ ...DEFAULT_EDIT_PROFILE_FORM });

  readonly profileForm = form(this.formModel$, (f) => {
    required(f.firstName);
    required(f.lastName);
    required(f.role);
    required(f.email);
    emailValidator(f.email);
    required(f.phoneNumber);
    validate(f.phoneNumber, ({ value }) => {
      const v = value();
      return !v || isValidUsPhone(v) ? null : { kind: 'phone' };
    });
  });

  readonly phoneValidationPattern = signal('(999) 999-9999').asReadonly();
  protected readonly phoneFormErrors = PHONE_FORM_ERRORS;

  open() {
    this.editProfileModalService.open();
  }

  close() {
    this.reset();
    this.editProfileModalService.close();
  }

  reset() {
    this.formModel$.set({ ...DEFAULT_EDIT_PROFILE_FORM });
  }

  save() {}
}
